/**
 * The ladders leaning on the hedges.
 *
 * Scenery, and only scenery: nothing here is a collider. The player walks straight
 * through a ladder and climbs it because `src/game/climbing.ts` says they are at one and
 * pushing into it — see the note in `placeLadders.ts` for why solid rungs would be worse
 * than useless. This file's whole job is to draw the thing the rule is about, in the place
 * the rule tests for.
 *
 * Rails and rungs are one `Instances` sharing a unit box, the idiom `MazeGrounds` uses for
 * the hedges and `RoomTrim` for the house's mouldings: twenty ladders is one draw call and
 * about 240 small instances. They are built in the ladder's own axes — local +x along the
 * hedge face, +z into it — and rotated into place by the yaw that maps local +z onto
 * `into`, so the same arithmetic works on all four sides of a cell.
 */

import { Instance, Instances } from '@react-three/drei';
import { useMemo } from 'react';
import { LADDER_DEPTH, LADDER_WIDTH, type Ladder } from '../game/maze/placeLadders';
import { getGradientMap } from './materials';

type Props = {
  ladders: readonly Ladder[];
};

/** Weathered garden timber: warm enough to separate from the hedge under a cold moon. */
const LADDER_COLOR = '#8a6a45';

/** Square section, and the thickest a rail can be before it reads as a fence post. */
const RAIL_THICKNESS = 0.07;

const RUNG_THICKNESS = 0.05;

/** A climbable rise. Also what decides how many instances a ladder costs. */
const RUNG_SPACING = 0.3;

type Piece = {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
};

function piecesOf(ladder: Ladder): Piece[] {
  // Local +z is `into`; local +x is the face axis, which is `into` turned a quarter turn.
  const yaw = Math.atan2(ladder.into.x, ladder.into.z);
  const side = { x: ladder.into.z, z: -ladder.into.x };
  const at = (lx: number, ly: number, lz: number): [number, number, number] => [
    ladder.base.x + side.x * lx + ladder.into.x * lz,
    ly,
    ladder.base.z + side.z * lx + ladder.into.z * lz,
  ];
  const rotation: [number, number, number] = [0, yaw, 0];

  const railOffset = LADDER_WIDTH / 2 - RAIL_THICKNESS / 2;
  const pieces: Piece[] = [
    {
      position: at(-railOffset, ladder.height / 2, 0),
      rotation,
      scale: [RAIL_THICKNESS, ladder.height, LADDER_DEPTH],
    },
    {
      position: at(railOffset, ladder.height / 2, 0),
      rotation,
      scale: [RAIL_THICKNESS, ladder.height, LADDER_DEPTH],
    },
  ];

  // Rungs sit on the player's side of the rails, where a foot would actually go, and stop
  // short of the crest so the top of the ladder is two rails and a gap to step through.
  const rungZ = -LADDER_DEPTH / 2 + RUNG_THICKNESS / 2;
  for (let y = RUNG_SPACING; y <= ladder.height - RUNG_SPACING / 2; y += RUNG_SPACING) {
    pieces.push({
      position: at(0, y, rungZ),
      rotation,
      scale: [LADDER_WIDTH - RAIL_THICKNESS, RUNG_THICKNESS, RUNG_THICKNESS],
    });
  }

  return pieces;
}

export function Ladders({ ladders }: Props) {
  const pieces = useMemo(() => ladders.flatMap(piecesOf), [ladders]);
  if (pieces.length === 0) return null;

  return (
    /* frustumCulled off for the reason the hedges have it off: an instanced mesh is culled
       by its base geometry's bounding sphere — a unit box at the origin — not by where its
       instances stand, so turning away from the origin would blink every ladder out. */
    <Instances
      frustumCulled={false}
      limit={pieces.length}
      range={pieces.length}
      castShadow
      receiveShadow
    >
      <boxGeometry args={[1, 1, 1]} />
      <meshToonMaterial color={LADDER_COLOR} gradientMap={getGradientMap()} />
      {pieces.map((piece, index) => (
        <Instance
          key={`ladder-${index}`}
          position={piece.position}
          rotation={piece.rotation}
          scale={piece.scale}
        />
      ))}
    </Instances>
  );
}
